import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import * as z from "zod/v4";
import * as schema from "./schema.ts";
import { YouTubeMediaDownloadFormat } from "../schema/youtube-media.js";

const identifiable = {
  id: z.uuid({ version: "v4" }),
};

export const Account = createSelectSchema(schema.accounts, {
  ...identifiable,
  name: z.string().trim().min(1).max(1024),
  importMeta: z.unknown().nullish(),
});
export interface Account extends z.output<typeof Account> {}

export const AccountInsert = createInsertSchema(schema.accounts);
export interface AccountInsert extends z.input<typeof AccountInsert> {}

export const BalanceAtDate = z.object({
  /** Balance of the account after all transfers that day have been completed */
  closingBalance: z.int64(),
  atDate: z.iso.date(),
});
export interface BalanceAtDate extends z.output<typeof BalanceAtDate> {}

export const AccountDailyBalancesReport = z.object({
  accountBalancesById: z.record(Account.shape.id, z.array(BalanceAtDate)),
  warnings: z.array(z.string()).nullish(),
});
export interface AccountDailyBalancesReport extends z.output<typeof AccountDailyBalancesReport> {}

export const Transfer = createSelectSchema(schema.transfers, {
  ...identifiable,
  description: z.string().trim().nonempty(),
  amount: z.uint32(),
});
export interface Transfer extends z.output<typeof Transfer> {}

export const TransferInsert = createInsertSchema(schema.transfers);
export interface TransferInsert extends z.input<typeof TransferInsert> {}

export const User = createSelectSchema(schema.users, {
  ...identifiable,
  emailAddress: z.email(),
});
export interface User extends z.output<typeof User> {}

export const UserInsert = createInsertSchema(schema.users);
export interface UserInsert extends z.input<typeof UserInsert> {}

export const Session = createSelectSchema(schema.sessions);
export interface Session extends z.output<typeof Session> {}

export const SignInToken = createSelectSchema(schema.signInTokens);
export interface SignInToken extends z.output<typeof SignInToken> {}

export const MediaProvider = z.enum(["youtube"]);
export type MediaProvider = z.output<typeof MediaProvider>;

export const MediaRequestedFormat = YouTubeMediaDownloadFormat;
export type MediaRequestedFormat = z.output<typeof MediaRequestedFormat>;

export const MediaTaskOperation = z.enum(["delete", "fetch"]);
export type MediaTaskOperation = z.output<typeof MediaTaskOperation>;

export const MediaTaskState = z.enum(["cancelled", "failed", "queued", "running", "succeeded"]);
export type MediaTaskState = z.output<typeof MediaTaskState>;

export const MediaTaskPhase = z.enum(["deleting", "downloading", "uploading", "validating"]);
export type MediaTaskPhase = z.output<typeof MediaTaskPhase>;

export const MediaRequest = createSelectSchema(schema.mediaRequests, {
  ...identifiable,
  provider: MediaProvider,
  requestedFormat: MediaRequestedFormat,
});
export interface MediaRequest extends z.output<typeof MediaRequest> {}

export const MediaTask = createSelectSchema(schema.mediaTasks, {
  ...identifiable,
  operation: MediaTaskOperation,
  phase: MediaTaskPhase.nullish(),
  state: MediaTaskState,
});
export interface MediaTask extends z.output<typeof MediaTask> {}
